import { useCallback } from 'react'
import { fbCustomEvent, META_STANDARD_EVENT_MAP } from 'services/facebook-pixel'
import { funnelFighters } from 'services/funnel-fighters'
import { intercom } from 'services/intercom'
import { analytics } from 'services/mixpanel/analytics-config'
import type { EventProperties } from 'services/mixpanel/types'

interface UseAnalyticsReturn {
  track: (eventName: string, properties?: EventProperties) => void
  identify: (userId: string) => void
  setUserProperties: (
    properties: Record<string, string | number | boolean | null | undefined>
  ) => void
  registerSuperProperties: (
    properties: Record<string, string | number | boolean | null | undefined>
  ) => void
  group: (groupType: string, groupKey: string) => void
  reset: () => void
  isInitialized: () => boolean
  getDistinctId: () => string | undefined
}

/**
 * Custom hook for analytics tracking
 * Provides a clean interface to the Mixpanel analytics service
 *
 * Usage with centralized events:
 * ```typescript
 * import { AnalyticsEvents } from '@mondaygpt/analytics-events'
 *
 * const { track } = useAnalytics()
 *
 * track(AnalyticsEvents.Authentication.LOGIN_STARTED, {
 *   method: 'email',
 *   referrer: '/dashboard'
 * })
 * ```
 */
export const useAnalytics = (): UseAnalyticsReturn => {
  const track = useCallback((eventName: string, properties?: EventProperties) => {
    analytics.track(eventName, properties)
    intercom.trackEvent(eventName, properties)
    fbCustomEvent(eventName, properties)
    funnelFighters.track(eventName, properties)
    // Fire the matching Meta standard event alongside the custom event so Meta
    // can optimize toward a real money signal. No-op for unmapped events.
    META_STANDARD_EVENT_MAP[eventName]?.(properties)
  }, [])

  const identify = useCallback((userId: string) => {
    analytics.identify(userId)
  }, [])

  const setUserProperties = useCallback(
    (properties: Record<string, string | number | boolean | null | undefined>) => {
      analytics.setUserProperties(properties)
    },
    []
  )

  const registerSuperProperties = useCallback(
    (properties: Record<string, string | number | boolean | null | undefined>) => {
      analytics.registerSuperProperties(properties)
    },
    []
  )

  const group = useCallback((groupType: string, groupKey: string) => {
    analytics.group(groupType, groupKey)
  }, [])

  const reset = useCallback(() => {
    analytics.reset()
  }, [])

  const isInitialized = useCallback(() => {
    return analytics.isInitialized()
  }, [])

  const getDistinctId = useCallback(() => {
    return analytics.getDistinctId()
  }, [])

  return {
    track,
    identify,
    setUserProperties,
    registerSuperProperties,
    group,
    reset,
    isInitialized,
    getDistinctId,
  }
}
